// deps
import React, { ReactElement, useState, useCallback, useMemo } from "react";

// internal deps
import { WALLET_OTP_LENGTH } from "@swiggy-private/payment-core";
import { InputFieldv4 } from "@swiggy-private/core-ui";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// constants
import { TEXTS } from "@payment-ui/constants/index";

// interfaces
import { OtpVerifyActions } from "@payment-ui/interfaces";

//helpers
import { isValidCode } from "@payment-ui/helpers/LinkAccount";

// test-ids
import { LINK_ACCOUNT_VIEW_TEST_IDS } from "@swiggy-private/payment-test-ids";

import styles from "../LinkAccountView.v4.scss";

interface OtpVerifyProps {
    walletCode: string;
    actions: OtpVerifyActions;
    "data-testid"?: string;
    ctaText?: string;
}

export default function OtpVerify(props: OtpVerifyProps): ReactElement {
    const [otp, setOtp] = useState("");
    const [error, setError] = useState(false);

    const { resendOtp, verifyOtp, verifyInProgress, resendInProgress } = props.actions;

    const getOtpLength = useCallback((): number | undefined => {
        if (isValidCode(props.walletCode)) {
            return WALLET_OTP_LENGTH[props.walletCode];
        }
    }, [props.walletCode]);

    const onInputChange = useCallback((otp: string): void => {
        setOtp(otp);
        setError(false);
    }, []);

    const onVerifyOtp = useCallback(
        async (event: React.MouseEvent): Promise<void> => {
            event.preventDefault();
            if (verifyInProgress) {
                return;
            }

            if (otp.length < Number(getOtpLength())) {
                setError(true);
                return;
            }

            try {
                await verifyOtp(otp);
            } catch (_) {
                setError(true);
            }
        },
        [getOtpLength, otp, verifyInProgress, verifyOtp],
    );

    const attrs = useMemo(
        () => ({
            maxLength: getOtpLength(),
            type: "tel",
            name: "verifyOtp",
            id: "verifyOtp",
            "data-testid": LINK_ACCOUNT_VIEW_TEST_IDS.OTP_INPUT,
        }),
        [getOtpLength],
    );

    return (
        <div className={styles.verifyOtp}>
            <div className={styles.verifyOtpInput}>
                <InputFieldv4 label="Enter OTP" onChange={onInputChange} attrs={attrs} error={error} value={otp} />
                <div className={styles.verifyOtpNoOTP}>
                    <SuperButton
                        buttonType="secondary"
                        buttonSize="small"
                        onClick={resendOtp}
                        data-testid={LINK_ACCOUNT_VIEW_TEST_IDS.RESEND_OTP_BUTTON}
                        isDisabled={resendInProgress}
                    >
                        {resendInProgress ? TEXTS.RESENDING_CODE : TEXTS.RESEND}
                    </SuperButton>
                </div>
            </div>

            <SuperButton
                onClick={onVerifyOtp}
                loading={verifyInProgress}
                data-testid={LINK_ACCOUNT_VIEW_TEST_IDS.VERIFY_AND_PROCEES_BUTTON}
                buttonType="primary"
                buttonSize="large"
                isDisabled={otp.length === 0}
            >
                {props.ctaText || TEXTS.VERIFY_N_LINK}
            </SuperButton>
        </div>
    );
}
